import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { smartFormApi } from '@/services';
import type { ApiResponse, PathParams } from '@/types/api-types';
import type {
  QueryOptions,
  QuerySuspenseOptions,
} from '@/types/tanstack-query-options';
import type { paths } from '~/generated/smart-form/api-schema';
import { formCodeDtoSchema } from '~/generated/smart-form/zod-schema';
import { migrateToLatest } from '../migrations/migrate-to-latest';
import { migratableTemplateContentSchema } from '../migrations/types';
import { templateSchema } from '../types/template/schema';
import { formTemplateStatusSchema } from './types/form-status-schema';

const categorySchema = zodRemoveNullable(formCodeDtoSchema);

const PATH_VAR = '{id}';
const API_PATH = `/v1/template/queryTemplate/${PATH_VAR}` as const;
export const QUERY_TEMPLATE_BY_ID_KEY = API_PATH;

type APIParams = PathParams<paths, typeof API_PATH>;
type APIResponse = ApiResponse<paths, typeof API_PATH>;

const apiResponseSchema = z.object({
  id: z.string(),
  templateCode: z.string(),
  name: z.string(),
  category: categorySchema,
  type: categorySchema,
  specialty: categorySchema,
  lockVersion: z.number(),
  changeVersion: z.number(),
  schemaVersion: z.string(),
  templateContent: templateSchema,
  modifyUserName: z.string(),
  modifyDatetime: z.string(),
  status: formTemplateStatusSchema,
  isOutdated: z.boolean(),
});
type APIResponseData = z.infer<typeof apiResponseSchema>;
export type QueryTemplateByIdData = APIResponseData;

export const queryTemplateById = async (id: string, signal?: AbortSignal) => {
  const { data: resp } = await smartFormApi.get<APIResponse>(
    API_PATH.replace(PATH_VAR, id),
    {
      signal,
    },
  );

  const migratable = migratableTemplateContentSchema.parse(
    resp.data?.templateContent,
  );
  const migrated = migrateToLatest(migratable);
  const data = {
    ...resp.data,
    templateContent: migrated,
    schemaVersion: migrated.version,
  };

  const result = apiResponseSchema.safeParse(data);
  if (result.error) {
    if (import.meta.env.DEV) {
      console.info(z.treeifyError(result.error));
    }
    throw new Error(result.error.message);
  }

  return result.data;
};

export const getSuspenseQueryTemplateByIdOptions = (id: APIParams['id']) => {
  return queryOptions({
    queryKey: [API_PATH, id] as const,
    queryFn: ({ signal }) => queryTemplateById(id, signal),
  });
};

export const useSuspenseQueryTemplateById = (
  id: string,
  options?: QuerySuspenseOptions<
    APIResponseData,
    readonly [typeof API_PATH, string]
  >,
) => {
  const defaultQueryOptions = getSuspenseQueryTemplateByIdOptions(id);
  return useSuspenseQuery({
    ...defaultQueryOptions,
    ...options,
  });
};

export const useQueryTemplateById = (
  id?: string,
  options?: QueryOptions<APIResponseData>,
) => {
  return useQuery({
    queryKey: [API_PATH, id],
    queryFn: id
      ? async ({ signal }) => {
          return await queryTemplateById(id, signal);
        }
      : skipToken,
    ...options,
  });
};

export const useEnsureQueryTemplateById = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (id: APIParams['id']) => {
      return await queryClient.ensureQueryData({
        queryKey: [API_PATH, id],
        queryFn: ({ signal }) => queryTemplateById(id, signal),
      });
    },
    [queryClient],
  );
};
